/**
 * useWorkspaceBus React hook · P2.6.a
 *
 * Wires up a WorkspaceBusClient + the zustand audit store + gap-fill on reconnect.
 * Components mount this once at the workspace route and consume via the store.
 *
 * codex-review fix history:
 *   R1 P1 · read FRESH high-water on reconnect; loop while has_more=true.
 *   R2 P1 · workspace switch resets store; replayCursor decoupled from
 *           highWaterAuditId so capped drains don't get bypassed.
 *   R3 P1 · always drain (even from 0) on every reconnect so new workspace
 *           gets caught up; capped drains save partial progress so retry
 *           resumes from where we left off; drains are workspace-guarded so
 *           late resolutions from a prior workspace can't poison the new one.
 */

import { useEffect, useRef } from 'react';
import { WorkspaceBusClient, fetchAuditSince, type WsEvent } from './ws-client';
import { useAuditStore } from '../stores/audit-store';

export interface UseWorkspaceBusOpts {
  backendUrl: string;
  workspaceId: string;
  token: string;
  /** Disable auto-connect (useful for tests). */
  enabled?: boolean;
  /** Max pages to pull per gap-fill cycle · defense against runaway. Default 20 (≈ 4000 events at limit=200). */
  maxGapPages?: number;
}

export interface AuditRefreshResult {
  completed: boolean;          // true if drained to has_more=false within max pages
  workspaceChanged: boolean;    // true if the store switched workspace mid-drain
  eventsFetched: number;        // raw event count returned by REST
  eventsAdded: number;          // count inserted after audit_id de-dupe
  lastSeenAuditId: number;      // highest audit_id seen in this drain (0 if none)
  replayCursor: number;
  highWaterAuditId: number;
  snapshotApplied?: boolean;
  snapshotNodeCount?: number;
}

export interface RefreshAuditStoreOpts {
  backendUrl: string;
  workspaceId: string;
  token: string;
  /** Max pages to pull per refresh cycle. Default 20 (≈ 4000 events at limit=200). */
  maxPages?: number;
  /** Test seam; production uses the shared audit store. */
  store?: typeof useAuditStore;
}

export interface RefreshSessionWorkflowStateOpts extends RefreshAuditStoreOpts {
  sessionId: string;
}

function trimBackendUrl(backendUrl: string): string {
  return backendUrl.replace(/\/$/, '');
}

export async function fetchWorkflowSnapshotEvent(opts: {
  backendUrl: string;
  token: string;
  sessionId: string;
  fetchImpl?: typeof fetch;
}): Promise<WsEvent> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  const url = `${trimBackendUrl(opts.backendUrl)}/api/sessions/${encodeURIComponent(opts.sessionId)}/workflow/snapshot`;
  const resp = await fetchImpl(url, {
    headers: { Authorization: `Bearer ${opts.token}` },
  });
  if (!resp.ok) {
    throw new Error(`workflow snapshot fetch failed: ${resp.status}`);
  }
  const body = await resp.json() as {
    workspace_id?: string;
    session_id?: string;
    snapshot_audit_id?: number;
    nodes?: unknown[];
    edges?: unknown[];
  };
  if (
    !body.workspace_id
    || !body.session_id
    || !Number.isSafeInteger(body.snapshot_audit_id)
    || (body.snapshot_audit_id ?? -1) < 0
    || !Array.isArray(body.nodes)
    || !Array.isArray(body.edges)
  ) {
    throw new Error('workflow snapshot malformed');
  }
  return {
    kind: 'workflow.snapshot',
    workspace_id: body.workspace_id,
    session_id: body.session_id,
    ts: Date.now(),
    payload: body as Record<string, unknown>,
  };
}

export async function refreshSessionWorkflowState(
  opts: RefreshSessionWorkflowStateOpts,
): Promise<AuditRefreshResult> {
  const store = opts.store ?? useAuditStore;
  const auditResult = await refreshAuditStore(opts);
  if (auditResult.workspaceChanged) return auditResult;

  const boundWorkspace = opts.workspaceId;
  const snapshotEvent = await fetchWorkflowSnapshotEvent({
    backendUrl: opts.backendUrl,
    token: opts.token,
    sessionId: opts.sessionId,
  });
  if (store.getState().workspaceId !== boundWorkspace) {
    return { ...auditResult, workspaceChanged: true, snapshotApplied: false, snapshotNodeCount: 0 };
  }
  const snapshotAuditId = typeof snapshotEvent.payload?.snapshot_audit_id === 'number'
    ? snapshotEvent.payload.snapshot_audit_id
    : null;
  const alreadyApplied = snapshotAuditId !== null && store.getState().events.some((evt) =>
    evt.kind === 'workflow.snapshot'
    && evt.session_id === opts.sessionId
    && evt.payload?.snapshot_audit_id === snapshotAuditId,
  );
  if (!alreadyApplied) {
    store.getState().pushEvent(snapshotEvent);
  }
  return {
    ...auditResult,
    snapshotApplied: !alreadyApplied,
    snapshotNodeCount: Array.isArray(snapshotEvent.payload?.nodes)
      ? snapshotEvent.payload.nodes.length
      : 0,
  };
}

/**
 * Pull audit-log truth into the shared audit store.
 *
 * This intentionally starts from `replayCursor`, not high-water. Live WS events
 * may advance high-water past an unfilled gap; replayCursor is the last audit id
 * confirmed by REST gap-fill, so manual sync can repair missed pushes while the
 * socket stayed open.
 */
export async function refreshAuditStore(opts: RefreshAuditStoreOpts): Promise<AuditRefreshResult> {
  const store = opts.store ?? useAuditStore;
  const maxPages = opts.maxPages ?? 20;

  store.getState().bindWorkspace(opts.workspaceId);
  const boundWorkspace = opts.workspaceId;
  const startState = store.getState();
  const startSeen = startState.seenAuditIds.size;
  const startCursor = startState.replayCursor;
  let cursor = startCursor;
  let lastSeen = 0;
  let eventsFetched = 0;

  for (let page = 0; page < maxPages; page++) {
    const { events, has_more } = await fetchAuditSince(opts.backendUrl, opts.token, cursor);
    if (events.length > 0) {
      eventsFetched += events.length;
      if (store.getState().workspaceId !== boundWorkspace) {
        return finishRefresh(store, startSeen, {
          completed: false,
          workspaceChanged: true,
          eventsFetched,
          lastSeenAuditId: 0,
        });
      }
      store.getState().pushEvents(events);
      const last = events[events.length - 1].audit_id;
      if (typeof last === 'number' && last > cursor) {
        cursor = last;
        lastSeen = last;
      } else {
        // Defensive · server returned events without audit_id or non-monotonic.
        return finishRefresh(store, startSeen, {
          completed: false,
          workspaceChanged: false,
          eventsFetched,
          lastSeenAuditId: lastSeen,
        });
      }
    }
    if (!has_more) {
      if (store.getState().workspaceId !== boundWorkspace) {
        return finishRefresh(store, startSeen, {
          completed: false,
          workspaceChanged: true,
          eventsFetched,
          lastSeenAuditId: 0,
        });
      }

      // Complete drain · advance replayCursor to absorb live events that
      // arrived during the drain. Live events bumped highWater but deliberately
      // did not advance replayCursor until REST confirms the contiguous range.
      const post = store.getState();
      const advanceTo = Math.max(lastSeen, post.highWaterAuditId, startCursor);
      if (advanceTo > post.replayCursor) {
        store.getState().setReplayCursor(advanceTo);
      }

      return finishRefresh(store, startSeen, {
        completed: true,
        workspaceChanged: false,
        eventsFetched,
        lastSeenAuditId: lastSeen,
      });
    }
  }

  console.warn(`[use-workspace-bus] gap-fill capped at ${maxPages} pages · cursor=${cursor}`);
  if (store.getState().workspaceId === boundWorkspace && lastSeen > 0) {
    // Capped drain must save partial progress; do not jump to highWater because
    // live events could be past the unfilled tail.
    const post = store.getState();
    if (lastSeen > post.replayCursor) {
      store.getState().setReplayCursor(lastSeen);
    }
  }

  return finishRefresh(store, startSeen, {
    completed: false,
    workspaceChanged: store.getState().workspaceId !== boundWorkspace,
    eventsFetched,
    lastSeenAuditId: lastSeen,
  });
}

function finishRefresh(
  store: typeof useAuditStore,
  startSeen: number,
  result: Pick<AuditRefreshResult, 'completed' | 'workspaceChanged' | 'eventsFetched' | 'lastSeenAuditId'>,
): AuditRefreshResult {
  const state = store.getState();
  return {
    ...result,
    eventsAdded: Math.max(0, state.seenAuditIds.size - startSeen),
    replayCursor: state.replayCursor,
    highWaterAuditId: state.highWaterAuditId,
  };
}

export function useWorkspaceBus(opts: UseWorkspaceBusOpts): void {
  const ref = useRef<WorkspaceBusClient | null>(null);
  const maxGapPages = opts.maxGapPages ?? 20;

  useEffect(() => {
    if (opts.enabled === false) return;

    // codex R2 P1 fix · bind workspace BEFORE building client so dedup +
    // cursor are scoped to this tenant. bindWorkspace is a no-op if already
    // bound to the same workspaceId.
    useAuditStore.getState().bindWorkspace(opts.workspaceId);

    // Capture for closure-stable comparison · workspace switch will rebind
    // store · async drains check this before any state mutation (R3 P1).
    const boundWorkspace = opts.workspaceId;

    const initialReplay = useAuditStore.getState().replayCursor;
    const client = new WorkspaceBusClient({
      backendUrl: opts.backendUrl,
      workspaceId: opts.workspaceId,
      token: opts.token,
      sinceAuditId: initialReplay,
      onEvent: (evt) => {
        // Cross-workspace defense (matches client-side filter · belt + suspenders)
        if (useAuditStore.getState().workspaceId === boundWorkspace) {
          useAuditStore.getState().pushEvent(evt);
        }
      },
      onState: async (state) => {
        if (useAuditStore.getState().workspaceId === boundWorkspace) {
          useAuditStore.getState().setConnectionState(state);
        }
        if (state !== 'open') return;

        // codex R3 P1 fix · always run drain on connect open · NOT only when
        // replayCursor > 0. New workspaces start at 0 · their first reconnect
        // (after WS dropping) needs to gap-fill from 0 too. Server returns
        // 0 events + has_more=false on first-ever empty workspace · drain is
        // a cheap no-op there.
        try {
          await refreshAuditStore({
            backendUrl: opts.backendUrl,
            token: opts.token,
            workspaceId: boundWorkspace,
            maxPages: maxGapPages,
          });
        } catch (e) {
          console.warn('[use-workspace-bus] gap-fill failed', e);
        }
      },
    });
    ref.current = client;
    client.connect();
    return () => {
      client.close();
      ref.current = null;
    };
    // Re-mount only on identity changes (workspace / token swap / enable toggle).
    // Inner reads of replayCursor / highWater / live state come from getState().
  }, [opts.backendUrl, opts.workspaceId, opts.token, opts.enabled, maxGapPages]);
}
